import _                                                                                         from 'underscore';
import React, { FC, useEffect, useRef, useState }                                                from 'react';
import { v4 as uuid, v5 as uuidv5 }                                                              from "uuid";
import { ENVIRONMENT_IS_PRODUCTION, ENVIRONMENT_IS_STAGING, ENVIRONMENT_LABEL, PRODUCT_VERSION } from '../../../../constants/SystemEnums';
import { useCurrentUser, usePrevious }                                                           from '../../../../hooks';
import { ScriptLoaderWithVisualBug, VendorLoaderProps }                                          from './ScriptLoaderWithVisualBug';

enum HEAPIO_ENV_IDS {
	TEST = '906220673',
	PROD = '3317080309',
}

const defaultProps: VendorLoaderProps = {
	id: ( ENVIRONMENT_IS_PRODUCTION || ENVIRONMENT_IS_STAGING )
	    ? HEAPIO_ENV_IDS.PROD
	    : HEAPIO_ENV_IDS.TEST
}

const HeapIOAnalytics: FC<VendorLoaderProps> = ( { id, ...props } ) => {

	let { currentUser }                 = useCurrentUser(),
	    prevCurrentUser                 = usePrevious( currentUser ),
	    [ providerKey, setProviderKey ] = useState( uuid() );

	useEffect( () => {

		// semaphore for the currentUser evaluation
		if( !prevCurrentUser?.id && currentUser?.id ){
			// console.log( 'Updating provider key for Heap.io...' );
			setProviderKey( uuid() );
		}

	}, [ currentUser ] );

	// heap identification
	useEffect( () => {

		let identity = ( currentUser ) ? {

			// namespaced key using domain and user id for unique keys across same accounts but different tenants / domains
			id:     uuidv5( window.location.hostname, currentUser.id ),
			custom: {
				firstName:           currentUser.firstName,
				lastName:            currentUser.lastName,
				email:               currentUser.emailAddress,
				tenant:              window.location.hostname,  // used for segmentation in LD
				user_id:             currentUser.id,            // actual user id in custom data to key on if needed in segmentation rules
				systemRole:          currentUser.systemRole,
				product_version:     PRODUCT_VERSION,
				product_environment: ENVIRONMENT_LABEL,
			},
		} : {
			id:     false,
			custom: {
				tenant:              window.location.hostname,
				product_version:     PRODUCT_VERSION,
				product_environment: ENVIRONMENT_LABEL
			},
		};

		if( window[ 'heap' ] ){

			if( currentUser ){

				// console.log( 'Identifying user to Heap.io...' );
				// console.log( `[${ identity.custom.email }]: ${ identity.id }` );
                window[ 'heap' ].identify( identity.id );
			}

            window[ 'heap' ].addUserProperties( identity.custom );
		}

	}, [ currentUser, providerKey ] );

	const initScript = () => {

		/*
		<script type="text/javascript">
	        window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
	        heap.load("906220673");
		</script>
		*/

		if( !window[ 'heap' ] ){

			// console.log( 'Initializing Heap.io...' );

			window[ 'heap' ] = window[ 'heap' ] || [];

            window[ 'heap' ].load = function( id: string, t = {} ){

				heap.appid  = id;
				heap.config = t;

				let methods = [
					"addEventProperties",
					"addUserProperties",
					"clearEventProperties",
					"identify",
					"resetIdentity",
					"removeEventProperty",
					"setEventProperties",
					"track",
					"unsetEventProperty"
				];

				for( var n = function( e ){return function(){heap.push( [ e ].concat( Array.prototype.slice.call( arguments, 0 ) ) )}}, p = methods, o = 0; o < p.length; o++ ){

					heap[ p[ o ] ] = n( p[ o ] );
				}
			};

		}

		if( window[ 'heap' ] && !heap.loaded ){

			// load correct environment id
			// console.log( `Loading Heap.io (ID:${ id })...` );
			heap.load( id );

		} else {

			// console.log( `Loaded Heap.io (ID:${ id }).` );
		}
	};

	let script = document.createElement( 'script' );

	script.async = true;
	script.type  = 'text/javascript';
	script.id    = `heapio-${ ENVIRONMENT_LABEL }-${ id }`;
	script.src   = `https://cdn.heapanalytics.com/js/heap-${ id }.js`;

	if( !document.getElementById( script.id ) ){

		document.getElementsByTagName( 'head' )[ 0 ].appendChild( script );
	}

	return ( id ? <ScriptLoaderWithVisualBug onLoad={ initScript } { ...props }/> : null );

}

HeapIOAnalytics.defaultProps = defaultProps;

export { HeapIOAnalytics }
